import * as Yup from 'yup';

import { getEstimatorCopy } from './copy';
import { isProjectDescriptionMeaningful } from './descriptionValidation';
import { IEstimatorFormValues } from './types';

export const estimatorInitialValues: IEstimatorFormValues = {
  adminExperiencePreference: null,
  about: '',
  apiEndpointCount: null,
  approximateBudgetRange: null,
  authenticationRequirements: [],
  catalogComplexity: null,
  catalogSize: null,
  checkoutComplexity: null,
  commerceLogicComplexity: null,
  companyName: '',
  complianceRequirements: [],
  contactName: '',
  email: '',
  estimatedUserCount: null,
  existingSystemMigration: null,
  expectedTimeline: null,
  externalIntegrations: [],
  howDidYouHearAboutUs: '',
  implementationPathPreference: null,
  inventoryComplexity: null,
  keyCapabilities: [],
  language: 'en',
  phoneNumber: '',
  preferredSurfaces: [],
  productModel: null,
  projectDescription: '',
  projectType: null,
  queueWorkflowCount: null,
  userRoles: null,
  webhookFlowCount: null,
};

export const getEstimatorStepFields = (projectTypeValue?: string) => {
  const baseSteps: Array<Array<keyof IEstimatorFormValues>> = [
    ['projectType', 'preferredSurfaces', 'projectDescription'],
    [
      'userRoles',
      'estimatedUserCount',
      'authenticationRequirements',
      'keyCapabilities',
      'apiEndpointCount',
      'webhookFlowCount',
      'queueWorkflowCount',
    ],
  ];

  if (projectTypeValue === 'webshop') {
    baseSteps.push([
      'productModel',
      'implementationPathPreference',
      'adminExperiencePreference',
      'commerceLogicComplexity',
      'catalogSize',
      'catalogComplexity',
      'checkoutComplexity',
      'inventoryComplexity',
    ]);
  }

  baseSteps.push(
    ['externalIntegrations', 'existingSystemMigration', 'complianceRequirements'],
    ['expectedTimeline', 'approximateBudgetRange'],
    ['contactName', 'email'],
  );

  return baseSteps;
};

export const getEstimatorSchema = (language: 'en' | 'hr') => {
  const copy = getEstimatorCopy(language);
  const optionSchema = Yup.object({
    label: Yup.string().required(),
    value: Yup.string().required(),
  }).required();
  const requireForWebshop = (message: string) =>
    Yup.mixed()
      .nullable()
      .when('projectType', {
        is: (value: IEstimatorFormValues['projectType']) => value?.value === 'webshop',
        otherwise: schema => schema.nullable(),
        then: schema => schema.required(message),
      });
  const requireForBackendPlatform = (message: string) =>
    Yup.mixed()
      .nullable()
      .when('projectType', {
        is: (value: IEstimatorFormValues['projectType']) => value?.value === 'backend-platform',
        otherwise: schema => schema.nullable(),
        then: schema => schema.required(message),
      });
  const requireWhenCapabilitySelected = (capability: string, message: string) =>
    Yup.mixed()
      .nullable()
      .when(['projectType', 'keyCapabilities'], {
        is: (
          projectType: IEstimatorFormValues['projectType'],
          keyCapabilities: IEstimatorFormValues['keyCapabilities'],
        ) => projectType?.value === 'backend-platform' && keyCapabilities.includes(capability),
        otherwise: schema => schema.nullable(),
        then: schema => schema.required(message),
      });

  return Yup.object({
    adminExperiencePreference: requireForWebshop(copy.validation.adminExperiencePreferenceRequired),
    about: Yup.string(),
    apiEndpointCount: requireForBackendPlatform(copy.validation.apiEndpointCountRequired),
    approximateBudgetRange: optionSchema.required(copy.validation.approximateBudgetRangeRequired),
    authenticationRequirements: Yup.array()
      .of(Yup.string())
      .min(1, copy.validation.authenticationRequirementsMin),
    catalogComplexity: requireForWebshop(copy.validation.catalogComplexityRequired),
    catalogSize: requireForWebshop(copy.validation.catalogSizeRequired),
    checkoutComplexity: requireForWebshop(copy.validation.checkoutComplexityRequired),
    commerceLogicComplexity: requireForWebshop(copy.validation.commerceLogicComplexityRequired),
    companyName: Yup.string(),
    complianceRequirements: Yup.array().of(Yup.string()),
    contactName: Yup.string().required(copy.validation.contactNameRequired),
    email: Yup.string().email(copy.validation.emailInvalid).required(copy.validation.emailRequired),
    estimatedUserCount: optionSchema.required(copy.validation.estimatedUserCountRequired),
    existingSystemMigration: optionSchema.required(copy.validation.existingSystemMigrationRequired),
    expectedTimeline: optionSchema.required(copy.validation.expectedTimelineRequired),
    externalIntegrations: Yup.array().of(Yup.string()),
    howDidYouHearAboutUs: Yup.string(),
    implementationPathPreference: requireForWebshop(
      copy.validation.implementationPathPreferenceRequired,
    ),
    inventoryComplexity: requireForWebshop(copy.validation.inventoryComplexityRequired),
    keyCapabilities: Yup.array().of(Yup.string()).min(1, copy.validation.keyCapabilitiesMin),
    language: Yup.mixed<'en' | 'hr'>().oneOf(['en', 'hr']).required(),
    phoneNumber: Yup.string(),
    preferredSurfaces: Yup.array().of(Yup.string()).min(1, copy.validation.preferredSurfacesMin),
    productModel: requireForWebshop(copy.validation.productModelRequired),
    queueWorkflowCount: requireWhenCapabilitySelected(
      'background-jobs',
      copy.validation.queueWorkflowCountRequired,
    ),
    projectDescription: Yup.string()
      .min(30, copy.validation.projectDescriptionMin)
      .test(
        'project-description-meaningful',
        copy.validation.projectDescriptionMeaningful,
        value => !value || isProjectDescriptionMeaningful(value),
      )
      .required(copy.validation.projectDescriptionRequired),
    projectType: optionSchema.required(copy.validation.projectTypeRequired),
    userRoles: optionSchema.required(copy.validation.userRolesRequired),
    webhookFlowCount: requireWhenCapabilitySelected(
      'webhook-orchestration',
      copy.validation.webhookFlowCountRequired,
    ),
  });
};
